// ═══ 말소대행신청 (B3 S2) — /main/m/request · ① 등록증 파일 → ② 대당 확인·공통 옵션 → 완료 ═══
// ① [직접 입력](Jin 10/28) = 파일 없는 빈 카드 1장을 붙이고 ② 로 — 칸은 ② 카드에서 바로 입력(OCR 없음, 월 한도 무관).
// 앱 malso_request_flow_screen.dart 와 같은 흐름·직렬화(requestLogic.buildQuickBody). 파일마다 즉시 OCR 병렬 —
// 한 장 실패(429 월 한도 포함)는 그 카드만 '인식 실패 · 직접 입력', 흐름은 멈추지 않는다(OCR 1장 = 월 한도 1건).
// 말소 탭 드롭존이 넘긴 파일은 useState 초기화에서 takeFiles() 1회. 대행사는 서버 자동(agencyId 미전송) —
// 부가서비스 칩은 GET /cancel-agencies 가 정확히 1곳일 때만. 오류 응답 원문(OCR raw 에 주민번호 가능)은
// 화면·로그에 올리지 않는다(문구 키만). 경로 문자열은 api.js 에만 둔다.
// 대행사(GET /my/malso-clients isBroker, W1a): ① 차마다 거래처를 고르고(다 골라야 [다음]) ④ 거래처 묶음마다 수거 주소를 고른다 —
// 공통 칸의 수거 주소 입력은 숨긴다. 목록을 읽는 동안은 [다음]을 잠근다. 비대행사·실패(404 포함)는 화면·body 모두 현행 그대로.
// props: { user, showToast }
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, R, S, FONT } from '../../theme';
import { useT } from '../../../i18n/I18nProvider';
import { useIsMobile } from '../../../utils/useMobile';
import ErrorText from '../../simple/parts/ErrorText';
import ConfirmSheet from '../../simple/parts/ConfirmSheet';
import { describeSimpleError } from '../../simple/errors';
import { card as cardBase, input as inputSt, textarea as textareaSt, CtaBar, btnNavy, btnPlain, btnDisabled } from '../../simple/screens/request/bits';
import { ocrImage, quickRequest, listAgencies, extraServices, listBuyers, getCompany, listMalsoClients, putMalsoClientPickup } from '../api';
import { takeFiles } from '../dropHandoff';
import { brokerPickupReady, buildQuickBody, defaultPickup, editField, groupCards, isHeic, isPdf, mapQuickError, mergeOcr, missingRequired, newCard, normalizeVin,
  pickedClient, precheckVins } from '../requestLogic';
import MalsoCarCard, { CardThumb, PerCarSheet, ServiceChips } from '../parts/MalsoCarCard';
import UnitPickSheet, { UnitRows, carLabel } from '../parts/UnitPickSheet';
import BrokerGroup from '../parts/BrokerGroup';
import JoinWaitCard from '../parts/JoinWaitCard';
import { refreshMalsoJoin } from '../joinRefresh';

var LIST = '/main/m/malso';
var ACCEPT = 'image/*,application/pdf,.pdf';
var COMMON = { cancelType: '수출말소', intakeMethod: '수거', urgent: false, services: [], buyerName: '', memo: '', pickupAddress: '' };

var page = { display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 760, margin: '0 auto', width: '100%' };
var headRow = { display: 'flex', alignItems: 'center', gap: 8 };
var backBtn = { border: 0, background: 'none', padding: 0, cursor: 'pointer', color: T.muted, fontSize: 18, lineHeight: 1, fontFamily: 'inherit' };
var titleSt = { fontSize: FONT.title, fontWeight: 800, letterSpacing: '-.35px', color: T.navy, margin: 0 };
var box = Object.assign({}, cardBase, { padding: 16 });
var boxTitle = { fontSize: FONT.cardTitle, fontWeight: 800, color: T.ink };
var col = { display: 'flex', flexDirection: 'column', gap: 5 };
var labSt = { fontSize: FONT.label, fontWeight: 700, color: T.muted };
var wrap = { display: 'flex', flexWrap: 'wrap', gap: 6 };
var stepRow = { display: 'flex', alignItems: 'center', gap: 8 };
var dot = { width: 22, height: 22, borderRadius: R.pill, flex: 'none', display: 'grid', placeItems: 'center', fontSize: 11.5, fontWeight: 800 };
var DOT = { done: { background: T.ok, color: T.card, border: '1px solid ' + T.ok }, on: { background: T.navy, color: T.card, border: '1px solid ' + T.navy },
  off: { background: T.card, color: T.faint, border: '1px solid ' + T.bd } };
var bar = { flex: 1, height: 2, minWidth: 16, background: T.line };
var stepLab = { fontSize: 12, fontWeight: 700, color: T.faint, whiteSpace: 'nowrap' };
var drop = { border: '1.5px dashed ' + T.accentBd, borderRadius: R.lg, background: T.accentTint, padding: '16px 20px', display: 'flex',
  alignItems: 'center', gap: 12, flexWrap: 'wrap', color: T.accentPress, fontSize: 13.5, fontWeight: 600 };
var thumbRow = { display: 'flex', alignItems: 'flex-start', gap: 12 };
var thumbGrid = { flex: 1, display: 'flex', flexWrap: 'wrap', gap: 10 };
var thumbCell = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 76 };
var cap = { fontSize: 10.5, color: T.muted, textAlign: 'center' };
var CAP = { reading: cap, failed: Object.assign({}, cap, { color: T.warn, fontWeight: 700 }), done: Object.assign({}, cap, { color: T.ok, fontWeight: 800 }) };
var countSt = { fontSize: 13, color: T.navy, whiteSpace: 'nowrap' };
var info = { fontSize: 11.5, color: T.muted, textAlign: 'center' };
var doneIcon = { width: 48, height: 48, borderRadius: R.pill, background: T.okTint, color: T.ok, display: 'grid', placeItems: 'center', fontSize: 24, fontWeight: 800 };
var doneRow = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: T.card, border: '1px solid ' + T.bd, borderRadius: R.card, fontSize: 12.5 };
var waitPill = { marginInlineStart: 'auto', fontSize: FONT.badge, fontWeight: 700, padding: '3px 8px', borderRadius: R.pill, background: T.track, color: T.muted };
var pickHintSt = Object.assign({}, info, { color: T.warn, fontWeight: 600 });

function withKey(o, k, v) { var n = Object.assign({}, o); n[k] = v; return n; }

function Stepper(props) {
  return (<div style={stepRow} data-testid="malso-stepper" data-step={props.step}>
    {['malsoModeStepCapture', 'malsoModeStepConfirm'].map(function (k, i) {
      var n = i + 1;
      var tone = n < props.step ? 'done' : n === props.step ? 'on' : 'off';
      return (<React.Fragment key={k}>
        {n > 1 ? <span style={Object.assign({}, bar, n <= props.step ? { background: T.ok } : null)} /> : null}
        <span style={Object.assign({}, dot, DOT[tone])}>{tone === 'done' ? '✓' : n}</span>
        <span style={Object.assign({}, stepLab, tone === 'on' ? { color: T.navy } : null)}>{props.t(k)}</span>
      </React.Fragment>);
    })}
  </div>);
}

function MalsoRequest(props) {
  var t = useT();
  var navigate = useNavigate();
  var isMobile = useIsMobile();
  var seqRef = useRef(0);
  var urlsRef = useRef([]);
  var aliveRef = useRef(true);
  var kickRef = useRef(false);
  var fileRef = useRef(null);

  var makeCards = function (files) {
    return files.map(function (f) {
      var url = '';
      if (!isPdf(f)) {
        try { url = window.URL.createObjectURL(f); urlsRef.current.push(url); } catch (e) { url = ''; }
      }
      seqRef.current += 1;
      return newCard(seqRef.current, f, url);
    });
  };

  var stCards = useState(function () { return makeCards(takeFiles()); }); var cards = stCards[0], setCards = stCards[1];
  var stStep = useState(1); var step = stStep[0], setStep = stStep[1];
  var stCommon = useState(COMMON); var common = stCommon[0], setCommon = stCommon[1];
  var stOpts = useState([]); var opts = stOpts[0], setOpts = stOpts[1];
  var stNames = useState([]); var names = stNames[0], setNames = stNames[1];
  var stOv = useState({}); var ov = stOv[0], setOv = stOv[1];
  var stPerCar = useState(false); var perCar = stPerCar[0], setPerCar = stPerCar[1];
  var stSheet = useState(null); var sheetKey = stSheet[0], setSheet = stSheet[1];
  var stBusy = useState(false); var busy = stBusy[0], setBusy = stBusy[1];
  var stErr = useState(''); var err = stErr[0], setErr = stErr[1];
  var stCreated = useState([]); var created = stCreated[0], setCreated = stCreated[1];
  var stDiscard = useState(false); var discard = stDiscard[0], setDiscard = stDiscard[1];
  // 대행사 — clients null = 읽는 중, 실패·404 = { isBroker:false }. unitOf {[card.key]:unitId} · pickupOf {[unitId]:{kind,address}} · added {[unitId]:true}
  var stClients = useState(null); var clients = stClients[0], setClients = stClients[1];
  var stUnitOf = useState({}); var unitOf = stUnitOf[0], setUnitOf = stUnitOf[1];
  var stPickupOf = useState({}); var pickupOf = stPickupOf[0], setPickupOf = stPickupOf[1];
  var stAdded = useState({}); var added = stAdded[0], setAdded = stAdded[1];
  var stPickKey = useState(null); var pickKey = stPickKey[0], setPickKey = stPickKey[1];
  var stAgency = useState(''); var agencyName = stAgency[0], setAgencyName = stAgency[1];
  var stAddr = useState(''); var companyAddr = stAddr[0], setCompanyAddr = stAddr[1];
  // 대표 링크 가입 업체 — 대행사 거래처 연결 전이면 흐름 대신 대기 안내(주소로 바로 들어와도). 서버 400 JOIN_PENDING 도 같은 안내 —
  //   그때 user.malsoJoin 이 없으면(옛 세션) 대행사명은 이 화면이 읽은 단일 대행사명.
  var stJoin = useState(false); var joinPending = stJoin[0], setJoinPending = stJoin[1];
  var join = (props.user && props.user.malsoJoin) || (joinPending ? { agencyName: agencyName } : null);
  var broker = !!(clients && clients.isBroker);

  var setC = function (k, v) { setCommon(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); };
  var loadClients = function () {
    return listMalsoClients().then(function (res) {
      if (aliveRef.current) setClients(res && res.isBroker ? res : { isBroker: false });
    }).catch(function () { if (aliveRef.current) setClients(function (p) { return p || { isBroker: false }; }); });
  };
  var update = function (key, fn) {
    if (!aliveRef.current) return;
    setCards(function (prev) { return prev.map(function (c) { return c.key === key ? fn(c) : c; }); });
  };
  var failed = function (c) { return Object.assign({}, c, { ocr: 'failed' }); };

  var runOcr = function (list) {
    list.forEach(function (c) {
      ocrImage(c.file).then(function (res) {
        var d = res && res.data;
        update(c.key, function (x) { return d && Object.keys(d).length ? mergeOcr(x, d) : failed(x); });
      }).catch(function () { update(c.key, failed); });   // 429 월 한도 포함 — 이 카드만, 원문은 버린다
    });
  };

  // 대기 안내 중이면 진입 때 한 번 다시 읽는다(말소 홈과 같음) — 연결됐으면 세션 갱신으로 흐름이 열린다.
  useEffect(function () { refreshMalsoJoin(props.user); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function () {
    aliveRef.current = true;
    var urls = urlsRef.current;
    if (!kickRef.current) { kickRef.current = true; runOcr(cards); }
    loadClients();
    listAgencies().then(function (list) {
      if (!Array.isArray(list) || list.length !== 1) return null;
      if (aliveRef.current) setAgencyName(String(list[0].name || ''));
      return extraServices(list[0].id).then(function (o) { if (aliveRef.current && Array.isArray(o)) setOpts(o); });
    }).catch(function () { /* 칩 섹션 숨김 — 신청은 막지 않는다 */ });
    listBuyers().then(function (rows) {
      var out = [];
      (Array.isArray(rows) ? rows : []).forEach(function (b) { var n = String((b && b.name) || '').trim(); if (n && out.indexOf(n) < 0) out.push(n); });
      if (aliveRef.current) setNames(out);
    }).catch(function () { /* 자동완성만 없음 */ });
    var u = props.user || {};
    getCompany(u.activeCompanyId || u.companyId).then(function (c) {
      var addr = String((c && c.pickupAddress) || '').trim();
      if (aliveRef.current) setCompanyAddr(String((c && c.addrKr) || '').trim());
      if (aliveRef.current && addr) setCommon(function (p) { return p.pickupAddress ? p : Object.assign({}, p, { pickupAddress: addr }); });
    }).catch(function () { /* 빈 칸 */ });
    return function () {
      aliveRef.current = false;
      urls.forEach(function (x) { try { window.URL.revokeObjectURL(x); } catch (e) { /* 이미 해제 */ } });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var addFiles = function (list) {
    var files = Array.prototype.slice.call(list || []);
    var ok = files.filter(function (f) { return !isHeic(f); });
    if (ok.length < files.length && props.showToast) props.showToast(t('malsoModeWebHeicUnsupported'), 'err');
    if (!ok.length) return;
    var added = makeCards(ok);
    setCards(function (prev) { return prev.concat(added); });
    runOcr(added);
  };

  var addManual = function () {
    seqRef.current += 1;
    var card = Object.assign(newCard(seqRef.current, null, ''), { ocr: 'done' });
    setCards(function (prev) { return prev.concat([card]); });
    setErr('');
    if (clients && !clients.isBroker) setStep(2);   // 대행사(와 목록 읽는 중)는 ① 에서 거래처부터
  };

  // ② 고르기 — 이 차(+ 체크 켜짐이면 아직 안 고른 차들)에 거래처를 넣고, 그 묶음 수거 주소가 없으면 기본값.
  // 새 거래처(isNew)는 목록에 없으면 바로 붙이고 '새 거래처' 표시, 목록은 다시 읽는다(409 = 이미 있던 거래처).
  var pickUnit = function (c, apply, isNew) {
    var known = clients.clients.filter(function (x) { return x.id === c.id; })[0];
    var client = known || c;
    if (isNew) {
      if (!known) {
        setAdded(function (p) { return withKey(p, c.id, true); });
        setClients(function (p) { return Object.assign({}, p, { clients: p.clients.concat([c]) }); });
      }
      loadClients();
    }
    setUnitOf(function (p) {
      var n = Object.assign({}, p);
      cards.forEach(function (x) { if (x.key === pickKey || (apply && !pickedClient(clients.clients, p, x.key))) n[x.key] = client.id; });
      return n;
    });
    setPickupOf(function (p) { return p[client.id] ? p : withKey(p, client.id, defaultPickup(client)); });
    setPickKey(null);
  };

  var setOverride = function (key, value) {
    setOv(function (p) { var n = Object.assign({}, p); if (value) n[key] = value; else delete n[key]; return n; });
  };

  // 신청 직전 게이트 1곳 — 주민/법인번호 13자리가 아닌 카드가 있으면 [신청] 비활성(말소신청서 필수). 버튼·submit 같은 값.
  var idsOk = cards.every(function (c) { return !missingRequired(c).length; });
  // 주민/법인번호만 남았으면 13자리 안내, 다른 빈 칸이 있으면 필수 칸 안내(앱과 같은 고르기).
  var onlyIdLeft = cards.every(function (c) { return !missingRequired(c).filter(function (f) { return f !== 'ownerId'; }).length; });
  // 대행사 — 모든 차 거래처 고름 + '다른 주소 입력' 묶음은 주소가 있어야 신청(비면 누를 때 토스트).
  var unpicked = broker ? cards.filter(function (c) { return !pickedClient(clients.clients, unitOf, c.key); }) : [];
  var groups = broker ? groupCards(cards, unitOf, clients.clients) : [];
  var pickOk = !broker || (!unpicked.length && brokerPickupReady(groups, pickupOf, common.intakeMethod));
  var submitSettled = !busy && cards.length > 0 && cards.every(function (c) { return c.ocr !== 'reading'; });
  var submitReady = submitSettled && idsOk && pickOk;
  var submitGateOnly = submitSettled && !submitReady;
  var submitSt = isMobile ? Object.assign({}, btnNavy, { flex: 1 }) : btnNavy;
  var submit = function () {
    if (busy || !idsOk || !pickOk || !cards.length || cards.some(function (c) { return c.ocr === 'reading'; })) return;
    var pre = precheckVins(cards);
    if (pre) { setErr(t(pre.key, pre.vars)); return; }
    setErr('');
    setBusy(true);
    setCards(function (prev) { return prev.map(function (c) { return c.exists ? Object.assign({}, c, { exists: false }) : c; }); });
    // 신청 성공 뒤 저장할 거래처 주소 — 수거 요청 + '다른 주소 입력' + 본인 아님. 실패해도 신청 결과는 성공.
    var saves = broker && common.intakeMethod !== '방문' ? groups.filter(function (g) {
      return !g.client.isSelf && (pickupOf[g.unitId] || {}).kind === 'custom';
    }) : [];
    quickRequest(buildQuickBody(cards, common, ov, broker ? { unitOf: unitOf, pickupOf: pickupOf } : undefined)).then(function (res) {
      if (!aliveRef.current) return;
      setCreated((res && res.created) || []);
      setStep(3);
      if (broker) {
        Promise.all(saves.map(function (g) { return putMalsoClientPickup(g.unitId, pickupOf[g.unitId].address.trim()); }))
          .catch(function () { /* 주소 저장 실패는 신청과 무관 */ }).then(loadClients);
      }
    }).catch(function (e) {
      if (!aliveRef.current) return;
      if (e && e.code === 'JOIN_PENDING') { setJoinPending(true); return; }
      var m = mapQuickError(e);
      if (m && m.unitIndex != null) {   // UNIT_NOT_FOUND — 그 거래처를 쓰던 차를 전부 비우고 ① 로(목록도 다시 읽는다)
        var badId = cards[m.unitIndex] ? unitOf[cards[m.unitIndex].key] : 0;
        setUnitOf(function (p) { var n = {}; Object.keys(p).forEach(function (k) { if (p[k] !== badId) n[k] = p[k]; }); return n; });
        loadClients();
        setStep(1);
        if (props.showToast) props.showToast(t(m.key, m.vars), 'err');
      }
      if (m && m.existing) {
        setCards(function (prev) { return prev.map(function (c) { return Object.assign({}, c, { exists: m.existing.indexOf(normalizeVin(c.vin)) >= 0 }); }); });
      }
      if (m && m.index != null && m.field) {   // OWNER_ID_REQUIRED · FIELD_REQUIRED — 그 카드 그 칸을 노란 칸으로(칸을 고치면 editField 가 지운다)
        setCards(function (prev) { return prev.map(function (c, i) { return i === m.index && c.weak.indexOf(m.field) < 0 ? Object.assign({}, c, { weak: c.weak.concat([m.field]) }) : c; }); });
      }
      setErr(m ? t(m.key, m.vars) : describeSimpleError(t, e));
    }).then(function () { if (aliveRef.current) setBusy(false); });
  };

  var again = function () {
    setCards([]); setOv({}); setPerCar(false); setErr(''); setCreated([]); setStep(1);
    setUnitOf({}); setPickupOf({}); setAdded({});
    setCommon(function (p) { return Object.assign({}, COMMON, { pickupAddress: p.pickupAddress }); });
  };

  var navBtn = function (label, onClick, enabled, id, plain) {
    var base = plain ? btnPlain : btnNavy;
    if (isMobile) base = Object.assign({}, base, { flex: 1 });   // 하단 고정 바 — 바 전체 폭 (검차 셸 StepVehicle 선례)
    return (<button type="button" style={enabled ? base : Object.assign({}, base, btnDisabled)} disabled={!enabled}
      data-testid={id} onClick={onClick}>{label}</button>);
  };
  var block = function (labelKey, child) { return <div style={col}><span style={labSt}>{t(labelKey)}</span>{child}</div>; };
  var seg = function (k, items) {
    return (<div style={wrap}>{items.map(function (it) {
      var on = common[k] === it[0];
      return (<button key={it[0]} type="button" style={on ? S.chipOn : S.chip} aria-pressed={on} data-opt={k + ':' + it[0]}
        onClick={function () { setC(k, it[0]); }}>{t(it[1])}</button>);
    })}</div>);
  };

  // ✕ — 카드(OCR 월 한도 소모분)나 입력이 있으면 버리기 확인 후에만 닫는다(앱 _close). 2단계 ‹ 은 확인 없이 1단계로.
  var close = function () {
    if (step === 2) { setStep(1); return; }
    var dirty = step === 1 && (cards.length > 0 || !!common.memo.trim() || !!common.buyerName.trim() || common.services.length > 0);
    if (dirty) setDiscard(true); else navigate(LIST);
  };

  var header = (<div style={headRow}>
    <button type="button" style={backBtn} disabled={busy} data-testid="malso-req-back" className={step === 2 ? 'ss-flipx' : undefined}
      aria-label={t(step === 2 ? 'actionBack' : 'actionClose')}
      onClick={close}>{step === 2 ? '‹' : '✕'}</button>
    <h2 style={titleSt}>{t('malsoModeRequestCta')}</h2>
  </div>);

  if (join) {
    return (<div style={page} data-testid="malso-join-block">
      <div style={headRow}>
        <button type="button" style={backBtn} data-testid="malso-req-back" aria-label={t('actionClose')}
          onClick={function () { navigate(LIST); }}>✕</button>
        <h2 style={titleSt}>{t('malsoModeRequestCta')}</h2>
      </div>
      <JoinWaitCard agencyName={join.agencyName} />
    </div>);
  }

  if (step === 3) {
    return (<div style={page} data-testid="malso-done">
      {header}
      <div style={Object.assign({}, box, { alignItems: 'center', textAlign: 'center' })}>
        <span style={doneIcon}>✓</span>
        <b style={{ fontSize: 17, color: T.ink }}>{t('malsoModeDoneTitle')}</b>
        <span style={{ fontSize: 13, color: T.muted }}>{t('malsoModeDoneBody')}</span>
      </div>
      {created.map(function (x) {
        return (<div key={x.id} style={doneRow}><b>{x.carNo || '-'}</b><span dir="ltr" style={{ color: T.muted }}>{x.vin}</span>
          <span style={waitPill}>{t('cancelAgencyStatusWaiting')}</span></div>);
      })}
      <CtaBar isMobile={isMobile}>
        {navBtn(t('malsoModeDoneMore'), again, true, 'malso-again', true)}
        {navBtn(t('malsoModeDoneGoList'), function () { navigate(LIST); }, true, 'malso-golist')}
      </CtaBar>
    </div>);
  }

  var settled = cards.filter(function (c) { return c.ocr !== 'reading'; }).length;
  var doneCount = cards.filter(function (c) { return c.ocr === 'done'; }).length;
  var exp = common.cancelType !== '폐차말소';
  var canPerCar = opts.length > 0 || exp;
  var sheetCard = cards.filter(function (c) { return c.key === sheetKey; })[0];
  var pickCard = broker ? cards.filter(function (c) { return c.key === pickKey; })[0] : null;
  var countText = t('malsoModeCaptureCount', { count: doneCount });

  var capture = (<React.Fragment>
    <div style={drop} data-testid="malso-drop" onDragOver={function (e) { e.preventDefault(); }}
      onDrop={function (e) { e.preventDefault(); addFiles(e.dataTransfer && e.dataTransfer.files); }}>
      {isMobile ? null : <span style={{ flex: 1, minWidth: 200 }}>{t('malsoModeWebDropTitle')}</span>}
      <button type="button" style={isMobile ? Object.assign({}, S.btnNavy, { flex: 1 }) : S.btnNavy} onClick={function () { if (fileRef.current) fileRef.current.click(); }}>{t('malsoModeWebDropPick')}</button>
      <button type="button" style={isMobile ? Object.assign({}, S.btn, { flex: 1 }) : S.btn} data-testid="malso-manual"
        onClick={addManual}>{t('malsoModeCaptureManual')}</button>
      <input ref={fileRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }} data-testid="malso-file"
        onChange={function (e) { addFiles(e.target.files); e.target.value = ''; }} />
    </div>
    {cards.length ? (<div style={thumbRow}>
      <div style={thumbGrid}>{cards.map(function (c) {
        return (<div key={c.key} style={thumbCell} data-testid={'thumb-' + c.key} data-ocr={c.ocr}>
          <CardThumb card={c} big />
          <span style={CAP[c.ocr]}>{c.ocr === 'reading' ? t('malsoModeCaptureReading') : c.ocr === 'failed' ? t('malsoModeCaptureFailed') : c.file ? '✓' : t('malsoModeCaptureManual')}</span>
        </div>);
      })}</div>
      {broker ? null : <b style={countSt}>{countText}</b>}
    </div>) : null}
    {broker && cards.length ? (<UnitRows t={t} head={countText + ' · ' + t('malsoModeBrokerPickEach')} cards={cards} unitOf={unitOf}
      clients={clients.clients} onOpen={setPickKey} />) : null}
    <div style={info}>{t('malsoModeInfoOnlyNeeded')}</div>
    {unpicked.length ? (<div style={pickHintSt} data-testid="malso-pick-hint">
      {t('malsoModeBrokerPickHint', { car: carLabel(t, unpicked[0]) })}</div>) : null}
    <CtaBar isMobile={isMobile}>
      {navBtn(t('malsoModeNextWithCount', { count: cards.length }), function () { setErr(''); setStep(2); },
        settled > 0 && clients !== null && !unpicked.length, 'malso-next')}
    </CtaBar>
    {pickCard ? (<UnitPickSheet t={t} card={pickCard} clients={clients.clients} selectedId={unitOf[pickCard.key] || 0}
      canAdd={(props.user || {}).currentRole === 'owner'} accountName={clients.accountName} agencyName={agencyName}
      onPick={pickUnit} onClose={function () { setPickKey(null); }} />) : null}
  </React.Fragment>);

  var carCard = function (c) {
    return (<MalsoCarCard key={c.key} t={t} index={cards.indexOf(c) + 1} card={c} hasOverride={!!ov[c.key]}
      onEdit={function (f, v) { update(c.key, function (x) { return editField(x, f, v); }); }}
      onRemove={function () { setCards(function (p) { return p.filter(function (x) { return x.key !== c.key; }); }); }}
      onPerCar={canPerCar && (perCar || ov[c.key]) ? function () { setSheet(c.key); } : null} />);
  };

  var confirm = (<React.Fragment>
    {broker ? groups.map(function (g) {   // ④ 거래처 묶음 — 본인 먼저, 묶음마다 수거 주소
      return (<BrokerGroup key={g.unitId} t={t} client={g.client} count={g.cards.length} isNew={!!added[g.unitId]}
        accountName={clients.accountName} collectAddr={String(common.pickupAddress).trim() || companyAddr} intakeMethod={common.intakeMethod}
        value={pickupOf[g.unitId]} onChange={function (v) { setPickupOf(function (p) { return withKey(p, g.unitId, v); }); }}>
        {g.cards.map(carCard)}</BrokerGroup>);
    }) : cards.map(carCard)}
    <div style={box} data-testid="malso-common">
      <div style={boxTitle}>{t('malsoModeCommonOptions', { count: cards.length })}</div>
      {seg('cancelType', [['수출말소', 'reqAdminCancelTypeExport'], ['폐차말소', 'reqAdminCancelTypeScrap']])}
      {exp ? block('buyersTitle', (<React.Fragment>
        <input style={inputSt} list="malso-buyers" value={common.buyerName} placeholder={t('malsoModeBuyerHint')} data-testid="malso-buyer"
          onChange={function (e) { setC('buyerName', e.target.value); }} />
        <datalist id="malso-buyers">{names.map(function (n) { return <option key={n} value={n} />; })}</datalist>
      </React.Fragment>)) : null}
      {seg('intakeMethod', [['수거', 'carCancelIntakePickup'], ['방문', 'carCancelIntakeVisit']])}
      <div style={wrap}><button type="button" style={common.urgent ? S.chipOn : S.chip} aria-pressed={common.urgent} data-opt="urgent"
        onClick={function () { setC('urgent', !common.urgent); }}>{t('carCancelUrgentLabel')}</button></div>
      {opts.length ? block('extraSvcTitle', <ServiceChips options={opts} value={common.services} onChange={function (v) { setC('services', v); }} />) : null}
      {common.intakeMethod === '수거' && !broker ? block('carCancelPickupSection', (<input style={inputSt} value={common.pickupAddress} data-testid="malso-pickup"
        onChange={function (e) { setC('pickupAddress', e.target.value); }} />)) : null}
      {block('cancelAgencyDetailFieldMemo', (<textarea style={textareaSt} value={common.memo} data-testid="malso-memo"
        onChange={function (e) { setC('memo', e.target.value); }} />))}
      {canPerCar ? (<button type="button" style={Object.assign({}, S.link, { alignSelf: 'flex-start' })} data-testid="malso-percar"
        aria-pressed={perCar} onClick={function () { setPerCar(!perCar); }}>{t('malsoModePerCarOptions') + ' ›'}</button>) : null}
    </div>
    <ErrorText>{err}</ErrorText>
    <CtaBar isMobile={isMobile}>
      {/* 필수 칸이 빠졌을 때는 꺼진 모양이지만 눌리고, 누르면 남은 칸을 토스트로만 알린다(안내 문구 없음 — 앱과 같게, Jin 10/28).
          처리 중·읽는 중은 진짜 disabled. */}
      <button type="button" data-testid="malso-submit" style={submitReady ? submitSt : Object.assign({}, submitSt, btnDisabled)}
        disabled={!submitReady && !submitGateOnly} aria-disabled={!submitReady}
        onClick={submitReady ? submit : function () {
          if (props.showToast) props.showToast(t(!idsOk ? (onlyIdLeft ? 'malsoModeOwnerIdRequired' : 'malsoModeRequiredMissing') : 'malsoModeBrokerPickupCustomNeeded'), 'err');
        }}>
        {busy ? t('malsoModeSubmitting') : t('malsoModeSubmitWithCount', { count: cards.length })}</button>
    </CtaBar>
    {sheetCard ? (<PerCarSheet t={t} card={sheetCard} options={opts} exportCancel={exp} value={ov[sheetKey]}
      onSave={function (v) { setOverride(sheetKey, v); setSheet(null); }} onClose={function () { setSheet(null); }} />) : null}
  </React.Fragment>);

  return (<div style={page} data-testid="malso-request">
    {header}
    <Stepper t={t} step={step} />
    {step === 1 ? capture : confirm}
    <ConfirmSheet open={discard} title={t('malsoSheetDiscardTitle')} sub={t('malsoSheetDiscardBody')} confirmLabel={t('actionClose')}
      onConfirm={function () { setDiscard(false); navigate(LIST); }} onClose={function () { setDiscard(false); }} />
  </div>);
}

export default MalsoRequest;
